import { HotkeysProvider } from '@clarion-blueprint-shim';
import { ErrorBoundary } from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactNode, Suspense } from 'react';
import { RouterProvider as AriaRouterProvider } from 'react-aria-components';
import {
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  useHref,
  useNavigate,
} from 'react-router';
import { RouterProvider } from 'react-router/dom';
import RequireAuth from './auth/RequireAuth';
import RequireWorkspace from './auth/RequireWorkspace';
import { UserStateProvider } from './auth/UserState';
import { WorkspaceProvider } from './auth/WorkspaceState';
import { ClarionToasterProvider } from './clarion/ClarionToaster.tsx';
import { MobileTopNavigation } from './components/navigation/MobileTopNavigation.tsx';
import { SideNavigation } from './components/navigation/SideNavigation.tsx';
import SkipLink from './components/navigation/SkipLink.tsx';
import GoogleAnalytics from './GoogleAnalytics.tsx';
import HomeRouter from './home/HomeRouter';
import HubSpotAnalytics from './HubSpotAnalytics.tsx';
import { sentryCreateBrowserRouter } from './sentry.ts';
import EmbedLoadingScreen from './shared/EmbedLoadingScreen';
import { GlobalSpinner, NavigationSpinner } from './shared/GlobalSpinner.tsx';
import OfflineStatusToast from './shared/OfflineStatusToast';
import RouteScrollToTop from './shared/RouteScrollToTop';
import supportLinks from './shared/supportLinks.ts';
import { AvailableLanguageProvider } from './translation/AvailableLanguageProvider';
import { ErrorView, RouterErrorBoundary } from './util/ErrorBoundary';
import { getFlag } from './util/flags';
import HotkeysDialog from './util/HotkeysDialog.tsx';
import useCloudFunctionWarmer from './util/useCloudFunctionWarmer.ts';
import {
  RequireIntegrationAccess,
  RequireNonIntegrationReviewer,
} from './workspace/RequireIntegrationAccess';

function RecapProviders({ children }: { children: ReactNode }) {
  useCloudFunctionWarmer();

  return (
    <UserStateProvider>
      <WorkspaceProvider>
        <AvailableLanguageProvider>{children}</AvailableLanguageProvider>
      </WorkspaceProvider>
    </UserStateProvider>
  );
}

function EmbeddedLayout() {
  useCloudFunctionWarmer();

  return (
    <AvailableLanguageProvider>
      <Suspense fallback={<EmbedLoadingScreen />}>
        <main className="w-full min-h-full flex flex-col relative">
          <NavigationSpinner />
          <Outlet />
        </main>
        <OfflineStatusToast />
      </Suspense>
    </AvailableLanguageProvider>
  );
}

function RootLayout() {
  return (
    <RecapProviders>
      <GoogleAnalytics />
      <HubSpotAnalytics />
      <SkipLink />
      <SideNavigation>
        <MobileTopNavigation />
        <main id="main" className="min-h-full min-w-0 flex-1">
          <Suspense fallback={<GlobalSpinner />}>
            <NavigationSpinner />
            <Outlet />
          </Suspense>
        </main>
      </SideNavigation>
      <OfflineStatusToast />
      <RouteScrollToTop />
    </RecapProviders>
  );
}

// Workaround for external links. See https://github.com/adobe/react-spectrum/issues/6397#issuecomment-2256960329
function useAbsoluteHref(path: string) {
  const relative = useHref(path);
  if (
    path.startsWith('https://') ||
    path.startsWith('http://') ||
    path.startsWith('mailto:') ||
    path.startsWith('tel:')
  ) {
    return path;
  }
  return relative;
}

function AriaProvider() {
  const navigate = useNavigate();
  return (
    <AriaRouterProvider navigate={navigate} useHref={useAbsoluteHref}>
      <Outlet />
      <ClarionToasterProvider />
    </AriaRouterProvider>
  );
}

const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <Route element={<AriaProvider />}>
      {/* Embedded transcript viewer needs to be outside the regular Providers and navigation */}
      <Route
        path="/embed/*"
        element={<EmbeddedLayout />}
        ErrorBoundary={RouterErrorBoundary}
        HydrateFallback={GlobalSpinner}
      >
        <Route path=":workspaceId/showcases/:showcaseId/*">
          <Route path="" lazy={() => import('./embed/EmbeddedShowcaseDetails.tsx')} />
          <Route path="search" lazy={() => import('./embed/EmbeddedShowcaseSearch.tsx')} />
          <Route path="files/:fileId" lazy={() => import('./embed/EmbeddedTranscriptViewer.tsx')} />
        </Route>
        <Route
          path=":workspaceId/files/:fileId"
          lazy={() => import('./embed/EmbeddedTranscriptViewer.tsx')}
        />
      </Route>
      <Route
        element={<RootLayout />}
        ErrorBoundary={RouterErrorBoundary}
        HydrateFallback={GlobalSpinner}
      >
        {/* This route is just to keep the RootLayout visible (if possible) when an error is thrown) */}
        <Route element={<Outlet />} ErrorBoundary={RouterErrorBoundary}>
          <Route path="/" element={<HomeRouter />} />

          <Route path="/licenses" lazy={() => import('./licenses/LicensesPage')} />
          <Route path="/login" lazy={() => import('./auth/AuthPage')} />
          <Route path="/signup" lazy={() => import('./auth/AuthPage')} />
          <Route path="/auth" lazy={() => import('./auth/AuthPage')} />
          {getFlag('sso') && (
            <Route path="/auth/sso/callback" lazy={() => import('./auth/sso/SamlCallback')} />
          )}

          <Route element={<RequireAuth />}>
            <Route
              path="/workspaces/:workspaceId/join"
              lazy={() => import('./workspace/WorkspaceJoinPage')}
            />
            <Route path="/workspaces/:workspaceId/*" element={<RequireWorkspace />}>
              <Route path="" element={<Navigate replace to="files" />} />
              <Route
                path="settings"
                lazy={() => import('./workspace/admin/WorkspaceSettingsWrapper')}
                ErrorBoundary={RouterErrorBoundary}
              >
                <Route path="" element={<Navigate to="general" replace />} />
                <Route path="general" lazy={() => import('./workspace/admin/GeneralPage')} />
                <Route path="members" lazy={() => import('./workspace/admin/MembersPage')} />
                <Route
                  path="integrations"
                  lazy={() => import('./workspace/admin/IntegrationsPage')}
                />
                {getFlag('captionAudit') && (
                  <Route
                    path="caption-audit"
                    lazy={() => import('./integrations/captionAudit/CaptionAuditDashboard.tsx')}
                  />
                )}
                {getFlag('captionAudit') && (
                  <Route
                    path="caption-audit/reports/:reportId"
                    lazy={() => import('./integrations/captionAudit/CaptionAuditReportPage.tsx')}
                  />
                )}
                <Route element={<RequireNonIntegrationReviewer />}>
                  <Route
                    path="analytics"
                    lazy={() => import('./requests/MediaRequestAnalyticsDashboard')}
                  />
                  <Route path="stats" lazy={() => import('./workspace/WorkspaceStatsPage')} />
                  <Route path="credit" lazy={() => import('./workspace/admin/CreditHistoryPage')} />
                  <Route path="plan" lazy={() => import('./workspace/admin/PlanPage')} />
                  <Route path="referrals" lazy={() => import('./workspace/admin/ReferralsPage')} />
                  <Route path="showcases" lazy={() => import('./showcases/ShowcasesPage')}>
                    <Route
                      ErrorBoundary={RouterErrorBoundary}
                      path=":showcaseId"
                      lazy={() => import('./showcases/ShowcaseDetailsPage')}
                    />
                    <Route path="*" lazy={() => import('./showcases/SelectShowcase')} />
                  </Route>
                </Route>
                <Route path="*" lazy={() => import('./util/NotFoundPage')} />
              </Route>
              <Route path="search" lazy={() => import('./search/WorkspaceSearchPage')} />
              <Route
                path="corrections"
                lazy={() => import('./corrections/PendingCorrectionsPage.tsx')}
              >
                <Route
                  path=":fileId/:correctionId"
                  lazy={() => import('./corrections/ReviewCorrection.tsx')}
                />
                <Route path="*" lazy={() => import('./corrections/SelectCorrection.tsx')} />
              </Route>
              <Route path="requests" lazy={() => import('./requests/PendingMediaRequestsPage.tsx')}>
                <Route
                  path=":fileId/:requestId"
                  lazy={() => import('./requests/ReviewRequest.tsx')}
                />
                <Route path="*" lazy={() => import('./requests/SelectRequest.tsx')} />
              </Route>
              <Route element={<RequireNonIntegrationReviewer />}>
                <Route
                  path="assignments"
                  lazy={() => import('./assignments/UserAssignmentsPage')}
                />
              </Route>
              <Route path="files/recap" lazy={() => import('./files/FilesPage')} />
              <Route path="files/recap/:fileId" lazy={() => import('./details/RecapDetailsPage')} />
              <Route
                path="files/panopto"
                element={<RequireIntegrationAccess integration="panopto" />}
              >
                <Route
                  path=""
                  lazy={() => import('./integrations/panopto/PanoptoVideosPage.tsx')}
                />
                <Route
                  path=":videoId"
                  lazy={() => import('./integrations/panopto/PanoptoDetailsPage.tsx')}
                />
              </Route>
              <Route
                path="files/youtube"
                element={<RequireIntegrationAccess integration="youtube" />}
              >
                <Route
                  path=""
                  lazy={() => import('./integrations/youtube/YouTubeVideosPage.tsx')}
                />
                <Route
                  path=":videoId"
                  lazy={() => import('./integrations/youtube/YouTubeDetailsPage.tsx')}
                />
              </Route>
              <Route
                path="files/brightcove"
                element={<RequireIntegrationAccess integration="brightcove" />}
              >
                <Route
                  path=""
                  lazy={() => import('./integrations/brightcove/BrightcoveVideosPage.tsx')}
                />
                <Route
                  path=":videoId"
                  lazy={() => import('./integrations/brightcove/BrightcoveDetailsPage.tsx')}
                />
              </Route>
              <Route
                path="files/googledrive"
                element={<RequireIntegrationAccess integration="googledrive" />}
              >
                <Route
                  path=""
                  lazy={() => import('./integrations/googleDrive/GoogleDriveFilesPage.tsx')}
                />
                <Route
                  path=":videoId"
                  lazy={() => import('./integrations/googleDrive/GoogleDriveDetailsPage.tsx')}
                />
              </Route>
              <Route path="files/box" element={<RequireIntegrationAccess integration="box" />}>
                <Route path="" lazy={() => import('./integrations/box/BoxFilesPage.tsx')} />
                <Route
                  path=":videoId"
                  lazy={() => import('./integrations/box/BoxDetailsPage.tsx')}
                />
              </Route>
              <Route
                path="files/kaltura"
                element={<RequireIntegrationAccess integration="kaltura" />}
              >
                <Route path="" lazy={() => import('./integrations/kaltura/KalturaFilesPage.tsx')} />
                <Route
                  path=":videoId"
                  lazy={() => import('./integrations/kaltura/KalturaEntryDetailsPage.tsx')}
                />
              </Route>
              {/* Fallback for when partner is unknown or existing emails */}
              <Route path="files/:fileId" lazy={() => import('./details/DetailsPageRedirect')} />
              <Route
                path="files/:fileId/analysis"
                lazy={() => import('./transcriptAccuracyAnalysis/TranscriptAccuracyAnalysis')}
              />
            </Route>

            <Route path="/account" lazy={() => import('./account/AccountPage')} />
            <Route path="/account/delete" lazy={() => import('./account/DeleteAccountPage')} />

            <Route
              path="/operator/:selectedWorkspaceId?"
              lazy={() => import('./operator/OperatorPage')}
            />
            <Route
              path="/operator/media-requests/analytics"
              lazy={() => import('./operator/MediaRequestOperatorDashboard')}
            />
            <Route
              path="/operator/stats"
              lazy={() => import('./operator/OperatorStatsPage')}
            />
          </Route>

          <Route path="/clarion" lazy={() => import('./clarion/ClarionReferencePage.tsx')} />

          <Route
            path="/help"
            Component={() => {
              window.location.replace(supportLinks.help);
              return null;
            }}
          />

          <Route
            path="/privacy"
            Component={() => {
              window.location.replace(supportLinks.privacy);
              return null;
            }}
          />
          <Route
            path="/terms"
            Component={() => {
              window.location.replace(supportLinks.terms);
              return null;
            }}
          />
          <Route
            path="/about"
            Component={() => {
              window.location.replace(supportLinks.about);
              return null;
            }}
          />

          <Route
            path="/caption-converter"
            lazy={() => import('./captionConverter/CaptionConverterPage')}
          />

          <Route path="*" lazy={() => import('./util/NotFoundPage')} />
        </Route>
      </Route>
    </Route>,
  ),
);

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1 } } });

export default function App() {
  return (
    <ErrorBoundary fallback={({ error }) => <ErrorView debugInfo={`${error}`} />}>
      <HotkeysProvider
        renderDialog={({ hotkeys, isDialogOpen }, { handleDialogClose }) => (
          <HotkeysDialog hotkeys={hotkeys} isOpen={isDialogOpen} onClose={handleDialogClose} />
        )}
      >
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </HotkeysProvider>
    </ErrorBoundary>
  );
}
